import { markdownHelpSource } from './markdownSource';
import { dbHelpSource } from './dbSource';
import { HELP_CATEGORIES, type HelpArticle, type HelpSource } from './types';

/**
 * The live help source: the database layered over the bundled markdown.
 *
 * Merge rule is by slug — a database row wins, a bundled article with no
 * database row still shows. So:
 *
 *   * an empty (or unreachable, or not-yet-migrated) table behaves exactly like
 *     the app did before the table existed
 *   * correcting one article means writing one row, no deploy
 *   * the app still works offline, on the copy it shipped with
 *
 * The bundle is the floor. That's deliberate: help content that disappears when
 * the network hiccups is worse than help content that's a release behind.
 */
const compositeHelpSource: HelpSource = {
    async listArticles() {
        const [bundled, remote] = await Promise.all([
            markdownHelpSource.listArticles(),
            // dbHelpSource never rejects, but belt and braces — the whole point
            // of this layer is that the bundle survives anything upstream.
            dbHelpSource.listArticles().catch(() => [] as HelpArticle[]),
        ]);

        const bySlug = new Map(bundled.map((a) => [a.slug, a]));
        for (const article of remote) bySlug.set(article.slug, article);

        return [...bySlug.values()].sort((a, b) => {
            const catDelta =
                HELP_CATEGORIES.indexOf(a.category) - HELP_CATEGORIES.indexOf(b.category);
            return catDelta !== 0 ? catDelta : a.order - b.order;
        });
    },

    async getArticle(slug: string) {
        return (await this.listArticles()).find((a) => a.slug === slug) ?? null;
    },
};

export const helpSource: HelpSource = compositeHelpSource;

export { HELP_CATEGORIES } from './types';
export type { HelpArticle, HelpCategory, HelpSource } from './types';
export { interpolateJargon } from './jargon';

/** Naive but adequate: title and summary weighted above body text. */
export function searchArticles(articles: HelpArticle[], query: string): HelpArticle[] {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    const terms = q.split(/\s+/);

    return articles
        .map((article) => {
            const title = article.title.toLowerCase();
            const summary = article.summary.toLowerCase();
            const body = article.body.toLowerCase();

            let score = 0;
            for (const term of terms) {
                if (title.includes(term)) score += 10;
                if (summary.includes(term)) score += 4;
                if (body.includes(term)) score += 1;
            }
            return { article, score };
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((r) => r.article);
}
